// src/services/adminService.js - UPDATED with Review Support
import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  query,
  orderBy,
  limit,
  where,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  getCountFromServer
} from 'firebase/firestore';
import enhancedUserService from '../services/enhancedUserService.js';

// Per-user subcollection reads run in parallel, capped so a large user base
// doesn't open hundreds of requests at once.
const USER_FETCH_CONCURRENCY = 20;

async function mapWithConcurrency(items, worker, concurrency = USER_FETCH_CONCURRENCY) {
  const results = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
}

class AdminService {

  // Check if current user is admin
  isAdmin() {
    const user = auth.currentUser;
    return user && user.email === 'amirsafary9@gmail.com';
  }

  // Initialize admin user in database (one-time setup)
  async initializeAdmin() {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const user = auth.currentUser;
      const adminRef = doc(db, 'admins', user.uid);

      await setDoc(adminRef, {
        email: user.email,
        role: 'admin',
        createdAt: serverTimestamp(),
        isActive: true,
        permissions: {
          users: true,
          reviews: true,
          analytics: true,
          moderation: true
        }
      });

      console.log('Admin user initialized in database');
      return { success: true, message: 'Admin initialized successfully' };
    } catch (error) {
      console.error('Error initializing admin:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if user has admin role in database
  async isAdminInDatabase(userId = null) {
    try {
      const currentUserId = userId || auth.currentUser?.uid;
      if (!currentUserId) return false;

      const adminRef = doc(db, 'admins', currentUserId);
      const adminDoc = await getDoc(adminRef);

      return adminDoc.exists() && adminDoc.data().role === 'admin' && adminDoc.data().isActive;
    } catch (error) {
      console.error('Error checking admin status in database:', error);
      return false;
    }
  }

  // Get all users overview
  async getAllUsers() {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));

      const perUser = await mapWithConcurrency(usersSnapshot.docs, async (userDoc) => {
        try {
          const userData = userDoc.data();

          // Latest study session, latest activity log and exam count are
          // independent, so fetch them together. The exam count is a server
          // aggregation — it no longer downloads every examResults document.
          const sessionsRef = collection(db, `users/${userDoc.id}/studySessions`);
          const logsRef = collection(db, `users/${userDoc.id}/activityLogs`);
          const examsRef = collection(db, `users/${userDoc.id}/examResults`);
          const [latestSession, latestLogTs, examCount] = await Promise.all([
            getDocs(query(sessionsRef, orderBy('startTime', 'desc'), limit(1))),
            // Ignore if missing index/subcollection; keep other sources.
            getDocs(query(logsRef, orderBy('timestamp', 'desc'), limit(1)))
              .then(snap => snap.docs[0]?.data()?.timestamp || null)
              .catch(() => null),
            getCountFromServer(examsRef).then(snap => snap.data().count)
          ]);

          const latestStudyActivity = latestSession.docs[0]?.data()?.startTime || null;
          const candidates = [latestStudyActivity, latestLogTs, userData.lastLoginAt, userData.updatedAt, userData.createdAt]
            .filter(Boolean);

          const lastActivity = candidates.length > 0
            ? candidates.reduce((latest, current) => {
                const latestMs = latest?.toMillis?.() ?? latest?.toDate?.()?.getTime?.() ?? new Date(latest).getTime();
                const currentMs = current?.toMillis?.() ?? current?.toDate?.()?.getTime?.() ?? new Date(current).getTime();
                return currentMs > latestMs ? current : latest;
              })
            : null;

          return {
            id: userDoc.id,
            ...userData,
            totalExams: examCount,
            lastActivity,
            lastStudyActivity: latestStudyActivity,
            lastLoginAt: userData.lastLoginAt || null,
            lastLoginDevice: userData.lastLoginDevice || 'unknown',
            isActive: this.isUserActive(userData.updatedAt)
          };
        } catch (userError) {
          console.warn(`Skipping admin aggregation for user ${userDoc.id}:`, userError?.message || userError);
          return null;
        }
      });

      return { success: true, users: perUser.filter(Boolean) };
    } catch (error) {
      console.error('Error getting all users:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user profile only (no heavy subcollections). Use getUserHistory for exams/sessions on demand.
  async getUserDetails(userId) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return { success: false, error: 'User not found' };
      }

      const userData = userDoc.data();
      return {
        success: true,
        userDetails: {
          profile: userData,
          studySessions: [],
          examResults: [],
          stats: {
            totalStudySessions: 0,
            totalExams: 0,
            averageExamScore: 0,
            studyStreak: userData.stats?.currentStreak ?? userData.studyStreak ?? 0
          }
        }
      };
    } catch (error) {
      console.error('Error getting user details:', error);
      return { success: false, error: error.message };
    }
  }

  // Load exam results and study sessions on demand (lazy history)
  async getUserHistory(userId, options = {}) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    const examLimit = options.examLimit ?? 50;
    const sessionLimit = options.sessionLimit ?? 50;

    try {
      const [examsSnapshot, sessionsSnapshot] = await Promise.all([
        getDocs(query(
          collection(db, `users/${userId}/examResults`),
          orderBy('completedAt', 'desc'),
          limit(examLimit)
        )),
        getDocs(query(
          collection(db, `users/${userId}/studySessions`),
          orderBy('startTime', 'desc'),
          limit(sessionLimit)
        ))
      ]);

      const examResults = examsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const studySessions = sessionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      return {
        success: true,
        examResults,
        studySessions,
        averageExamScore: this.calculateAverageScore(examResults)
      };
    } catch (error) {
      console.error('Error getting user history:', error);
      return { success: false, error: error.message, examResults: [], studySessions: [] };
    }
  }

  // Get activity logs from user subcollections
  async getActivityLogs(limitCount = 50) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const allLogs = [];

      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        try {
          const logsRef = collection(db, 'users', userId, 'activityLogs');
          // Try with orderBy first; if index is missing, fall back to getDocs without orderBy
          let logsSnapshot;
          try {
            const logsQuery = query(logsRef, orderBy('timestamp', 'desc'), limit(limitCount));
            logsSnapshot = await getDocs(logsQuery);
          } catch (indexError) {
            if (indexError?.code === 'failed-precondition' || indexError?.message?.includes('index')) {
              logsSnapshot = await getDocs(logsRef);
            } else {
              throw indexError;
            }
          }

          logsSnapshot.docs.forEach(doc => {
            allLogs.push({
              id: doc.id,
              userId,
              ...doc.data()
            });
          });
        } catch (error) {
          console.warn(`No activity logs for user ${userId}:`, error.message);
        }
      }

      allLogs.sort((a, b) => {
        const timeA = a.timestamp?.toMillis?.() || a.timestamp?.toDate?.()?.getTime?.() || 0;
        const timeB = b.timestamp?.toMillis?.() || b.timestamp?.toDate?.()?.getTime?.() || 0;
        return timeB - timeA;
      });

      return { success: true, logs: allLogs.slice(0, limitCount) };
    } catch (error) {
      console.error('Error getting activity logs:', error);
      return { success: false, error: error.message };
    }
  }

  // Get system statistics including reviews
  async getSystemStats() {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const totalUsers = users.length;
      const now = new Date();
      const premiumUsers = users.filter(user => {
        const status = user.subscriptionStatus || 'free';
        if (status !== 'active') return false;
        const endDate = user.subscriptionEndDate?.toDate?.() ?? user.subscriptionEndDate;
        if (!endDate) return true;
        return new Date(endDate) > now;
      }).length;
      const activeUsers = users.filter(user => this.isUserActive(user.updatedAt)).length;

      // Get total exams across all users
      let totalExams = 0;
      let totalStudySessions = 0;

      // Count-only aggregations, in parallel — no subcollection documents downloaded.
      await mapWithConcurrency(usersSnapshot.docs, async (userDoc) => {
        try {
          const [examsCount, sessionsCount] = await Promise.all([
            getCountFromServer(collection(db, `users/${userDoc.id}/examResults`)),
            getCountFromServer(collection(db, `users/${userDoc.id}/studySessions`))
          ]);
          totalExams += examsCount.data().count;
          totalStudySessions += sessionsCount.data().count;
        } catch (userError) {
          console.warn(`Skipping stats aggregation for user ${userDoc.id}:`, userError?.message || userError);
        }
      });

      // Get review statistics
      const reviewsSnapshot = await getDocs(collection(db, 'reviews'));
      const reviews = reviewsSnapshot.docs.map(doc => doc.data());
      const totalReviews = reviews.length;
      const publishedReviews = reviews.filter(r => r.status === 'published').length;
      const reportedReviews = reviews.filter(r => r.reportCount > 0).length;

      let averageRating = 0;
      if (publishedReviews > 0) {
        const totalRating = reviews
          .filter(r => r.status === 'published')
          .reduce((sum, r) => sum + r.rating, 0);
        averageRating = (totalRating / publishedReviews).toFixed(1);
      }

      return {
        success: true,
        stats: {
          totalUsers,
          premiumUsers,
          activeUsers,
          totalExams,
          totalStudySessions,
          totalReviews,
          publishedReviews,
          reportedReviews,
          averageRating,
          premiumRate: totalUsers ? Math.round((premiumUsers / totalUsers) * 100) : 0,
          activityRate: Math.round((activeUsers / totalUsers) * 100)
        }
      };
    } catch (error) {
      console.error('Error getting system stats:', error);
      return { success: false, error: error.message };
    }
  }

  // Admin actions - modify user data
  async updateUserData(userId, updateData) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updateData,
        adminModified: true,
        adminModifiedAt: serverTimestamp(),
        adminModifiedBy: auth.currentUser.email
      });

      return { success: true, message: 'User data updated successfully' };
    } catch (error) {
      console.error('Error updating user data:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete user's specific data
  async deleteUserExam(userId, examId) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      await deleteDoc(doc(db, `users/${userId}/examResults`, examId));
      return { success: true, message: 'Exam deleted successfully' };
    } catch (error) {
      console.error('Error deleting exam:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteUserSession(userId, sessionId) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      await deleteDoc(doc(db, `users/${userId}/studySessions`, sessionId));
      return { success: true, message: 'Study session deleted successfully' };
    } catch (error) {
      console.error('Error deleting session:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete user's review (admin override)
  async deleteUserReview(userId, reviewId) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      await deleteDoc(doc(db, 'reviews', reviewId));

      // Log the action
      await this.logAdminAction('delete_user_review', {
        userId,
        reviewId,
        reason: 'Admin deletion'
      });

      return { success: true, message: 'Review deleted successfully' };
    } catch (error) {
      console.error('Error deleting user review:', error);
      return { success: false, error: error.message };
    }
  }

  // Fix email verification status for all users (one-time admin action)
  async fixEmailVerificationStatus() {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      console.log('Starting email verification fix...');
      const usersSnapshot = await getDocs(collection(db, 'users'));
      let fixedCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data();

        try {
          // Note: This requires Firebase Admin SDK for server-side access
          // For now, we'll assume users who can login are verified

          if (userData.lastLoginAt && userData.emailVerified !== true) {
            // If user has logged in recently, they must be verified
            await updateDoc(doc(db, 'users', userId), {
              emailVerified: true,
              verificationFixedAt: serverTimestamp()
            });
            fixedCount++;
            console.log(`Fixed verification for user: ${userData.email}`);
          }
        } catch (error) {
          console.error(`Error fixing user ${userId}:`, error);
        }
      }

      console.log(`Fixed ${fixedCount} users`);
      return { success: true, fixedCount };
    } catch (error) {
      console.error('Error fixing email verification:', error);
      return { success: false, error: error.message };
    }
  }

  // Log admin actions for audit trail
  async logAdminAction(action, details = {}) {
    if (!this.isAdmin()) return;

    try {
      const adminLogRef = collection(db, 'adminLogs');
      await addDoc(adminLogRef, {
        action,
        details,
        adminEmail: auth.currentUser.email,
        adminUid: auth.currentUser.uid,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  }

  // Get admin action logs
  async getAdminLogs(limitCount = 50) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const logsRef = collection(db, 'adminLogs');
      const logsQuery = query(logsRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const logsSnapshot = await getDocs(logsQuery);

      const logs = logsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, logs };
    } catch (error) {
      console.error('Error getting admin logs:', error);
      return { success: false, error: error.message };
    }
  }

  // --- HELPER METHODS ---

  isUserActive(lastUpdate) {
    if (!lastUpdate) return false;
    const now = new Date();
    const lastUpdateDate = lastUpdate.toDate ? lastUpdate.toDate() : new Date(lastUpdate);
    const daysDiff = (now - lastUpdateDate) / (1000 * 60 * 60 * 24);
    return daysDiff <= 7; // Active if updated within last 7 days
  }

  calculateAverageScore(examResults) {
    if (!examResults.length) return 0;
    const total = examResults.reduce((sum, exam) => {
      const pct = exam.percentage;
      if (pct != null) return sum + pct;
      const score = exam.overallScore ?? exam.totalScore;
      const max = exam.maxScore;
      if (score != null && max != null && max > 0) return sum + (score / max) * 100;
      return sum;
    }, 0);
    return Math.round(total / examResults.length);
  }

  // Get all consultations for admin (upcoming and recent)
  async getConsultationsForAdmin(limitCount = 100) {
    if (!this.isAdmin()) throw new Error('Admin access required');

    try {
      const snapshot = await getDocs(collection(db, 'consultations'));
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const dateA = a.date || '';
        const dateB = b.date || '';
        if (dateB !== dateA) return dateB.localeCompare(dateA);
        return (b.timeSlot || '').localeCompare(a.timeSlot || '');
      });
      const consultations = list.slice(0, limitCount);
      return { success: true, consultations };
    } catch (error) {
      console.error('Error getting consultations:', error);
      return { success: false, error: error.message, consultations: [] };
    }
  }
}

export default new AdminService();